import { useState } from 'react';
import { WarningIcon } from '@phosphor-icons/react';

import { UserRole } from '@/api-models/UserRole';
import { useAuth } from '@/contexts/auth-context';

import { type AgentTurn, useAgentSessionStore } from '../../stores/agent-session-store';
import { usePopoverPanel } from '../../hooks/usePopoverPanel';
import { splitReasoning } from '../../stores/reasoning';
import { type ComposedBlock, parseComposedMessage } from '../../stores/selection-store';

import { AgentMark } from './AgentMark';
import { AgentSteps } from './AgentSteps';
import { AskUserToolCard } from './AskUserToolCard';
import { AssistantMarkdown } from './AssistantMarkdown';
import { ImageGallery } from './ImageLightbox';
import { GoalNotice } from './GoalNotice';
import { JobReportNotice } from './JobReportNotice';
import { ReasoningBlock } from './ReasoningBlock';
import { SELECTION_KIND } from './selection-kinds';
import { LiveActivityLine, ThinkingIndicator } from './ThinkingIndicator';
import { toolMeta } from './tool-metadata';

const ASSISTANT_NAME = 'Zooid Atlas';

/**
 * The kinds of context a message can carry, in the order the thread shows them: what changes how
 * the whole message reads — a plan being revised, a question being answered — leads.
 *
 * One list drives both the chips and the annotations inside the bubble, so a passage is numbered
 * `2` in the same place either way. That correspondence is the only thing tying a comment in the
 * bubble to the passage it is about.
 */
const KINDS = [
  { key: 'passages', ...SELECTION_KIND.plan, one: 'plan passage', many: 'plan passages' },
  { key: 'quotes', ...SELECTION_KIND.quote, one: 'quote', many: 'quotes' },
  { key: 'elements', ...SELECTION_KIND.element, one: 'element', many: 'elements' },
  { key: 'code', ...SELECTION_KIND.code, one: 'snippet', many: 'snippets' },
  { key: 'files', ...SELECTION_KIND.file, one: 'file', many: 'files' },
] as const;

type Kind = (typeof KINDS)[number];

/**
 * The context a selection attached — Preview-tab elements, code snippets, quoted messages or
 * plan passages — behind a click.
 *
 * A chip rather than a permanent block: the selector chains and markup are for the
 * agent, and inlining them buried the one line the user actually wrote.
 *
 * Prose blocks are rendered rather than quoted as source. A quoted message or a plan passage is
 * markdown lifted out of a rendered document, so showing the raw `<plan-passage>` wrapper around
 * its source is showing the user the wire format instead of the thing they picked.
 */
function SelectionChip({ kind, blocks }: { kind: Kind; blocks: ComposedBlock[] }) {
  const { open, toggle, triggerProps, panelProps } = usePopoverPanel();
  const { Icon } = kind;
  const noun = blocks.length === 1 ? kind.one : kind.many;

  return (
    <div className="relative flex justify-end" {...triggerProps}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-2xs font-medium transition-colors ${
          open
            ? 'border-brand bg-brand-tint text-brand-text'
            : 'border-brand-border bg-brand-tint text-brand-text hover:border-brand'
        }`}
      >
        <Icon size={11} />
        {blocks.length} {noun}
      </button>
      {open && (
        <div {...panelProps}>
          <div className="max-h-80 w-115 max-w-[80vw] overflow-auto rounded-xl border border-line-strong bg-surface-2 text-left shadow-menu">
            {/* Sticky, so scrolling through several passages never leaves the reader wondering
                what the list they are inside of is. */}
            <div className="sticky top-0 z-10 flex items-center gap-1.5 border-b border-line-subtle bg-surface-3 px-3 py-1.5 text-2xs font-medium text-fg-secondary">
              <Icon size={11} />
              <span>
                {blocks.length} {noun}
              </span>
              <span className="ml-auto font-normal text-fg-tertiary">Esc to close</span>
            </div>

            {blocks.map((block, i) => (
              <div key={i} className={i > 0 ? 'border-t border-line' : undefined}>
                {/* Numbered only when there is more than one — a lone "1." is noise, but without
                    them a run of passages reads as one continuous document, and the numbers are
                    what the bubble's comments point back at. */}
                {blocks.length > 1 && <div className="px-3 pt-2 text-2xs font-semibold text-fg-tertiary">{i + 1}</div>}
                <div className="px-3 py-2.5">
                  {kind.prose ? (
                    <AssistantMarkdown content={block.body} />
                  ) : (
                    <pre className="font-mono text-2xs leading-relaxed whitespace-pre-wrap text-fg-secondary">
                      {block.body}
                    </pre>
                  )}
                </div>
                {/* The user's own words, kept with the block they were written against and
                    tinted apart from it: above is what they picked, below is what they said. */}
                {block.note && (
                  <div className="border-t border-line-subtle bg-surface-3 px-3 py-2">
                    <div className="mb-0.5 text-2xs font-medium text-fg-tertiary">Your {kind.said}</div>
                    <p className="text-xs whitespace-pre-wrap text-fg">{block.note}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * A comment in the bubble, under a one-line excerpt of what it was written about.
 *
 * Without the excerpt the notes arrive as a run of unattributed sentences — "make a fancy design
 * system" above "i also want easter egg on the page" — and which passage each belongs to is only
 * recoverable by opening the chip and guessing. The excerpt is the same text the chip carries and
 * the number is the chip's own, so the bubble reads on its own and still points somewhere exact.
 */
function Annotation({ kind, block, index, total }: { kind: Kind; block: ComposedBlock; index: number; total: number }) {
  const { Icon } = kind;

  return (
    <div className="border-l-2 border-brand-border pl-2.5">
      <div className="flex items-center gap-1 text-2xs font-medium text-fg-tertiary">
        <Icon size={11} className="shrink-0" />
        {total > 1 && <span>{index + 1}</span>}
        <span className={`min-w-0 truncate font-normal ${kind.prose ? 'italic' : 'font-mono'}`}>{block.label}</span>
      </div>
      <p className="whitespace-pre-wrap">{block.note}</p>
    </div>
  );
}

function UserTurn({ turn }: { turn: AgentTurn }) {
  const { me } = useAuth();
  // Platform-level Zooid-staff gate (same check as AdminGuard / ToolCallRow).
  const isStaff = me.role === UserRole.Admin;
  const [showRaw, setShowRaw] = useState(false);

  const composed = parseComposedMessage(turn.content);
  // Held apart from `composed`, so the staff toggle below stays rendered while the raw prompt is
  // showing — deriving it from the same value would hide the way back out.
  const selection = showRaw ? null : composed;
  // Every note in the message, paired with the block it was written against and numbered as its
  // chip numbers it. Notes read as part of what the user said, so they belong in the bubble
  // rather than only in a chip — and when nothing was typed under the context, they are the
  // whole message.
  const annotations = selection
    ? KINDS.flatMap((kind) =>
        selection[kind.key].map((block, index, all) => ({ kind, block, index, total: all.length })),
      ).filter(({ block }) => block.note)
    : [];

  return (
    <div className="flex flex-col items-end gap-1.5">
      {selection &&
        KINDS.map((kind) =>
          selection[kind.key].length > 0 ? (
            <SelectionChip key={kind.key} kind={kind} blocks={selection[kind.key]} />
          ) : null,
        )}
      <ImageGallery images={turn.images ?? []} />
      <div className="flex max-w-135 flex-col gap-3 rounded-2xl rounded-br-sm border border-line-subtle bg-surface-3 px-4 py-3 text-sm leading-relaxed text-fg">
        {annotations.map((annotation) => (
          <Annotation key={`${annotation.kind.key}-${annotation.index}`} {...annotation} />
        ))}
        {selection ? (
          selection.request && <p className="whitespace-pre-wrap">{selection.request}</p>
        ) : (
          <p className="whitespace-pre-wrap">{turn.content}</p>
        )}
      </div>
      {composed && isStaff && (
        <button
          type="button"
          onClick={() => setShowRaw((raw) => !raw)}
          aria-pressed={showRaw}
          className="text-2xs text-fg-tertiary underline-offset-2 hover:underline"
        >
          {showRaw ? 'Hide raw prompt' : 'Show raw prompt'}
        </button>
      )}
    </div>
  );
}

function AssistantTurn({ turn }: { turn: AgentTurn }) {
  // Which question, if any, is still answerable. Read from the store rather than inferred from
  // this turn's position, so an ask left behind by an earlier run renders as abandoned — matching
  // exactly what the server will accept an answer for.
  const pendingAskId = useAgentSessionStore((s) => s.pendingAsk?.toolCallId ?? null);
  // Live-only, and shared by every turn on screen — but only a streaming turn renders it, and
  // there is at most one of those.
  const retry = useAgentSessionStore((s) => s.retry);

  const streaming = turn.status === 'streaming';
  const runningCall = turn.toolCalls.find((tc) => tc.status === 'running');

  // Naming what is happening beats a generic loader, so a concrete state wins over the word cycle.
  const activityLabel = retry
    ? `Retrying (attempt ${retry.attempt} of ${retry.maxAttempts})…`
    : runningCall
      ? `Running ${toolMeta(runningCall.name).humanTitle}…`
      : undefined;

  // Nothing to show yet — surface the animated thinking loader in place of the header until the
  // first text, reasoning or tool call arrives.
  const pending = streaming && turn.toolCalls.length === 0 && !turn.content && !turn.reasoning;

  if (pending) {
    return (
      <div className="flex flex-col gap-3">
        <ThinkingIndicator label={activityLabel} />
      </div>
    );
  }

  const { reasoning: scraped, answer, reasoningStreaming } = splitReasoning(turn.content);
  // A model that streams reasoning in its own channel wins over the `<think>` scraping, which
  // finds nothing for it. Reloaded turns carry no streamed reasoning, so they fall back.
  const reasoning = turn.reasoning?.trim() || scraped;
  const reasoningLive = streaming && (turn.reasoning ? !answer : reasoningStreaming);

  // Questions are hoisted out of the collapsible steps group: a Q&A exchange is conversation, not
  // mechanics, and `AgentSteps` folds itself away behind "Ran N steps" once the turn settles.
  // The cost is that strict interleaving with the greps and reads around it is lost — acceptable,
  // since an ask is a boundary in a turn rather than a step inside it.
  const asks = turn.toolCalls.filter((tc) => tc.name === 'ask_user');
  const steps = turn.toolCalls.filter((tc) => tc.name !== 'ask_user');

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        {/* The mark breathes for as long as the turn is streaming. It used to be static the moment
            the first tool call landed, which is most of a run — so the thread's only sign of life
            was a row appearing every so often. */}
        <AgentMark pulsing={streaming} />
        <span className="text-xs font-semibold text-fg">{ASSISTANT_NAME}</span>
      </div>
      <div className="flex flex-col gap-3 pl-8.5">
        <AgentSteps toolCalls={steps} streaming={streaming} />
        {asks.map((toolCall) => (
          <AskUserToolCard key={toolCall.id} toolCall={toolCall} live={pendingAskId === toolCall.id} />
        ))}
        {reasoning && <ReasoningBlock text={reasoning} streaming={reasoningLive} />}
        {answer && <AssistantMarkdown content={answer} />}
        {/* Last, so it always reads as "and now…". Between rounds — after a tool result, before
            the next call — nothing else on screen is moving, and that gap is most of a long run. */}
        {streaming && <LiveActivityLine label={activityLabel} />}
      </div>
    </div>
  );
}

function SystemTurn({ turn }: { turn: AgentTurn }) {
  return (
    <div className="flex items-center justify-center gap-1.5 text-xs text-fg-tertiary">
      <WarningIcon size={13} />
      {turn.content}
    </div>
  );
}

export function TurnBubble({ turn }: { turn: AgentTurn }) {
  if (turn.role === 'user') return <UserTurn turn={turn} />;
  if (turn.role === 'system') return <SystemTurn turn={turn} />;
  if (turn.role === 'job') return <JobReportNotice content={turn.content} />;
  if (turn.role === 'goal') return <GoalNotice content={turn.content} />;
  return <AssistantTurn turn={turn} />;
}
